import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adApi } from '../../../api/ad.api';
import { StrategyDailyMetric, BannerPositionMetric, TouchpointDailyRow } from '../../../types/Ad.api.type';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Copy, Check } from 'lucide-react';

type ReportMode = 'daily' | 'period';

const getYesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const get14DaysAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 14);
  return d.toISOString().slice(0, 10);
};

const getToday = () => new Date().toISOString().slice(0, 10);

/** null/undefined → "-" */
const v = (val: number | null | undefined): string => (val != null ? String(val) : '-');
const vPct = (val: number | null | undefined): string => (val != null ? `${val}%` : '-');

// ===================== 텍스트 생성 함수 =====================

/** 일일 보고 텍스트 생성 */
function buildDailyReport(
  date: string,
  metric: StrategyDailyMetric | undefined,
  touchpointRow: TouchpointDailyRow | undefined,
  touchpointLabels: Record<string, string>,
  snapshot: { circulatingPieces: number; totalIssued: number; totalExchanged: number; usersWithBalance: number } | undefined,
  rewardTypes: { rewardType: string; label: string; count: number; pieces: number }[],
): string {
  if (!metric) return `=== 나만그래 일일 보고 (${date}) ===\n\n데이터 없음`;

  const lines: string[] = [];
  lines.push(`=== 나만그래 일일 보고 (${date}) ===`);

  // 토스 광고 성과
  lines.push('');
  lines.push('[토스 광고 성과]');
  lines.push(`리워드: 노출${v(metric.tossRewardImpressions)}, 시청${vPct(metric.tossRewardCtr)}, eCPM${v(metric.tossRewardEcpm)}, 수익${v(metric.tossRewardRevenue)}`);
  lines.push(`배너: 노출${v(metric.tossBannerImpressions)}, 시청${vPct(metric.tossBannerCtr)}, eCPM${v(metric.tossBannerEcpm)}, 수익${v(metric.tossBannerRevenue)}`);
  lines.push(`이미지: 노출${v(metric.tossImageImpressions)}, 시청${vPct(metric.tossImageCtr)}, eCPM${v(metric.tossImageEcpm)}, 수익${v(metric.tossImageRevenue)}`);
  lines.push(`전면: 노출${v(metric.tossInterstitialImpressions)}, 시청${vPct(metric.tossInterstitialCtr)}, eCPM${v(metric.tossInterstitialEcpm)}, 수익${v(metric.tossInterstitialRevenue)}`);
  lines.push(`합산: 신규${v(metric.newUsers)}, 전체${v(metric.totalUsers)}, D1신규${vPct(metric.retentionD1New)}, D1기존${vPct(metric.retentionD1Total)}`);

  // 위치별 배너
  if (metric.bannerPositions?.length) {
    lines.push('');
    lines.push('[위치별 배너]');
    metric.bannerPositions.forEach((bp: BannerPositionMetric) => {
      lines.push(`${bp.positionLabel}: 노출${v(bp.impressions)}, CTR${vPct(bp.ctr)}, eCPM${v(bp.ecpm)}, 수익${v(bp.revenue)}`);
    });
  }

  // 위치별 이미지배너
  if (metric.imagePositions?.length) {
    lines.push('');
    lines.push('[위치별 이미지배너]');
    metric.imagePositions.forEach((ip: BannerPositionMetric) => {
      lines.push(`${ip.positionLabel}: 노출${v(ip.impressions)}, CTR${vPct(ip.ctr)}, eCPM${v(ip.ecpm)}, 수익${v(ip.revenue)}`);
    });
  }

  // 자체 지표
  lines.push('');
  lines.push('[자체 지표]');
  lines.push(`활성유저:${metric.activeUsers}, 투표:${metric.totalVotes}, 리워드시청:${metric.rewardWatch}, 출석:${metric.totalAttendance}`);
  lines.push(`실제가입:${metric.actualSignups}, 1인당광고:${metric.adWatchPerUser}`);

  // 조각경제
  lines.push('');
  lines.push('[조각경제]');
  lines.push(`조각발행:${metric.pieceIssuance}, 토스포인트지급:${metric.tossPointPayment}, 환전:${metric.exchangeAmount}`);
  if (snapshot) {
    lines.push(`[스냅샷] 유통:${snapshot.circulatingPieces}, 누적발행:${snapshot.totalIssued}, 누적환전:${snapshot.totalExchanged}, 잔액보유유저:${snapshot.usersWithBalance}`);
  }

  // 터치포인트별 리워드 시청
  if (touchpointRow) {
    lines.push('');
    lines.push('[터치포인트별 리워드 시청]');
    const parts = Object.entries(touchpointRow.counts)
      .filter(([, cnt]) => cnt > 0)
      .map(([tp, cnt]) => {
        const pct = touchpointRow.total > 0 ? ((cnt / touchpointRow.total) * 100).toFixed(1) : '0';
        const label = touchpointLabels[tp] || tp;
        return `${label}:${cnt}(${pct}%)`;
      });
    lines.push(parts.join(', '));
    lines.push(`합계:${touchpointRow.total}`);
  }

  // 타입별 조각 상세
  if (rewardTypes.length > 0) {
    lines.push('');
    lines.push('[타입별 조각 상세]');
    rewardTypes.forEach(rt => {
      lines.push(`${rt.label}: ${rt.count}건 ${rt.pieces}조각`);
    });
  }

  return lines.join('\n');
}

/** 기간 보고 텍스트 생성 */
function buildPeriodReport(
  from: string,
  to: string,
  metrics: StrategyDailyMetric[],
  touchpointRows: TouchpointDailyRow[],
  touchpointLabels: Record<string, string>,
  snapshot: { circulatingPieces: number; totalIssued: number; totalExchanged: number; usersWithBalance: number } | undefined,
  rewardDaily: { date: string; types: { rewardType: string; label: string; count: number; pieces: number }[]; totalPieces: number }[],
): string {
  const lines: string[] = [];
  lines.push(`=== 나만그래 기간 보고 (${from} ~ ${to}) ===`);

  // 일별 토스 광고 성과
  lines.push('');
  lines.push('[일별 토스 광고 성과]');
  lines.push('날짜 | R노출 R시청 R-eCPM R수익 | B노출 B시청 B-eCPM B수익 | I노출 I시청 I-eCPM I수익 | F노출 F시청 F-eCPM F수익 | 신규 전체 D1신규 D1기존');
  metrics.forEach(m => {
    const d = m.date.slice(5); // MM-DD
    lines.push(
      `${d} | ${v(m.tossRewardImpressions)} ${vPct(m.tossRewardCtr)} ${v(m.tossRewardEcpm)} ${v(m.tossRewardRevenue)}` +
      ` | ${v(m.tossBannerImpressions)} ${vPct(m.tossBannerCtr)} ${v(m.tossBannerEcpm)} ${v(m.tossBannerRevenue)}` +
      ` | ${v(m.tossImageImpressions)} ${vPct(m.tossImageCtr)} ${v(m.tossImageEcpm)} ${v(m.tossImageRevenue)}` +
      ` | ${v(m.tossInterstitialImpressions)} ${vPct(m.tossInterstitialCtr)} ${v(m.tossInterstitialEcpm)} ${v(m.tossInterstitialRevenue)}` +
      ` | ${v(m.newUsers)} ${v(m.totalUsers)} ${vPct(m.retentionD1New)} ${vPct(m.retentionD1Total)}`
    );
  });

  // 위치별 배너 (일별)
  const hasBannerPositions = metrics.some(m => m.bannerPositions?.length);
  if (hasBannerPositions) {
    // 모든 위치 수집
    const allPositions = new Map<string, string>();
    metrics.forEach(m => {
      m.bannerPositions?.forEach(bp => allPositions.set(bp.position, bp.positionLabel));
    });

    lines.push('');
    lines.push('[일별 위치별 배너]');
    const posLabels = Array.from(allPositions.values());
    lines.push(`날짜 | ${posLabels.map(l => `${l}(노출/CTR/eCPM/수익)`).join(' | ')} | 합산`);
    metrics.forEach(m => {
      const d = m.date.slice(5);
      const posMap = new Map((m.bannerPositions || []).map(bp => [bp.position, bp]));
      const parts = Array.from(allPositions.keys()).map(pos => {
        const bp = posMap.get(pos);
        return bp ? `${v(bp.impressions)}/${vPct(bp.ctr)}/${v(bp.ecpm)}/${v(bp.revenue)}` : '-/-/-/-';
      });
      // 합산
      const totalImp = (m.bannerPositions || []).reduce((s, bp) => s + (bp.impressions || 0), 0);
      const totalRev = (m.bannerPositions || []).reduce((s, bp) => s + (bp.revenue || 0), 0);
      lines.push(`${d} | ${parts.join(' | ')} | ${totalImp}/${totalRev}`);
    });
  }

  // 위치별 이미지배너 (일별)
  const hasImagePositions = metrics.some(m => m.imagePositions?.length);
  if (hasImagePositions) {
    const allImgPositions = new Map<string, string>();
    metrics.forEach(m => {
      m.imagePositions?.forEach(ip => allImgPositions.set(ip.position, ip.positionLabel));
    });

    lines.push('');
    lines.push('[일별 위치별 이미지배너]');
    const posLabels = Array.from(allImgPositions.values());
    lines.push(`날짜 | ${posLabels.map(l => `${l}(노출/CTR/eCPM/수익)`).join(' | ')} | 합산`);
    metrics.forEach(m => {
      const d = m.date.slice(5);
      const posMap = new Map((m.imagePositions || []).map(ip => [ip.position, ip]));
      const parts = Array.from(allImgPositions.keys()).map(pos => {
        const ip = posMap.get(pos);
        return ip ? `${v(ip.impressions)}/${vPct(ip.ctr)}/${v(ip.ecpm)}/${v(ip.revenue)}` : '-/-/-/-';
      });
      const totalImp = (m.imagePositions || []).reduce((s, ip) => s + (ip.impressions || 0), 0);
      const totalRev = (m.imagePositions || []).reduce((s, ip) => s + (ip.revenue || 0), 0);
      lines.push(`${d} | ${parts.join(' | ')} | ${totalImp}/${totalRev}`);
    });
  }

  // 일별 자체 지표
  lines.push('');
  lines.push('[일별 자체 지표]');
  lines.push('날짜 | 활성 투표 리워드 출석 가입 1인당');
  metrics.forEach(m => {
    const d = m.date.slice(5);
    lines.push(`${d} | ${m.activeUsers} ${m.totalVotes} ${m.rewardWatch} ${m.totalAttendance} ${m.actualSignups} ${m.adWatchPerUser}`);
  });

  // 일별 조각경제
  lines.push('');
  lines.push('[일별 조각경제]');
  lines.push('날짜 | 발행 지급 환전');
  metrics.forEach(m => {
    const d = m.date.slice(5);
    lines.push(`${d} | ${m.pieceIssuance} ${m.tossPointPayment} ${m.exchangeAmount}`);
  });

  // 조각경제 스냅샷
  if (snapshot) {
    lines.push('');
    lines.push('[조각경제 스냅샷 (현재)]');
    lines.push(`유통:${snapshot.circulatingPieces}, 누적발행:${snapshot.totalIssued}, 누적환전:${snapshot.totalExchanged}, 잔액보유유저:${snapshot.usersWithBalance}`);
  }

  // 일별 터치포인트
  if (touchpointRows.length > 0) {
    lines.push('');
    lines.push('[일별 터치포인트 리워드 시청]');
    const allTps = Object.keys(touchpointRows[0]?.counts || {});
    const tpHeaders = allTps.map(tp => touchpointLabels[tp] || tp);
    lines.push(`날짜 | ${tpHeaders.join(' ')} | 합계`);
    touchpointRows.forEach(row => {
      const d = row.date.slice(5);
      const vals = allTps.map(tp => String(row.counts[tp] || 0));
      lines.push(`${d} | ${vals.join(' ')} | ${row.total}`);
    });
  }

  // 일별 타입별 조각 상세
  if (rewardDaily.length > 0) {
    lines.push('');
    lines.push('[일별 타입별 조각 상세]');
    rewardDaily.forEach(rd => {
      if (rd.types.length === 0) return;
      const d = rd.date.slice(5);
      const parts = rd.types.map(t => `${t.label}:${t.count}건/${t.pieces}조각`);
      lines.push(`${d} | ${parts.join(', ')} | 합계:${rd.totalPieces}조각`);
    });
  }

  return lines.join('\n');
}

// ===================== 컴포넌트 =====================

export const ReportDashboardTab = () => {
  const [mode, setMode] = useState<ReportMode>('daily');
  const [copied, setCopied] = useState(false);

  // 일일: 어제, 기간: 14일
  const yesterday = getYesterday();
  const today = getToday();
  const fourteenDaysAgo = get14DaysAgo();

  const dailyFrom = yesterday;
  const dailyTo = yesterday;
  const periodFrom = fourteenDaysAgo;
  const periodTo = today;

  const from = mode === 'daily' ? dailyFrom : periodFrom;
  const to = mode === 'daily' ? dailyTo : periodTo;

  // 전략 메트릭
  const { data: metricsData, isLoading: metricsLoading } = useQuery({
    queryKey: ['reportMetrics', from, to],
    queryFn: () => adApi.getStrategyMetrics(from, to).then(res => res.data),
    meta: { hideGlobalLoader: true },
  });

  // 터치포인트 일별
  const { data: touchpointData, isLoading: touchpointLoading } = useQuery({
    queryKey: ['reportTouchpoint', from, to],
    queryFn: () => adApi.getTouchpointDailyMatrix(from, to).then(res => res.data),
    meta: { hideGlobalLoader: true },
  });

  // 조각경제 스냅샷
  const { data: snapshotData } = useQuery({
    queryKey: ['reportPieceSnapshot'],
    queryFn: () => adApi.getPieceSnapshot().then(res => res.data),
    meta: { hideGlobalLoader: true },
  });

  // 리워드 브레이크다운
  const { data: rewardData, isLoading: rewardLoading } = useQuery({
    queryKey: ['reportRewardBreakdown', from, to],
    queryFn: () => adApi.getRewardBreakdown(from, to).then(res => res.data),
    meta: { hideGlobalLoader: true },
  });

  const touchpointLabelMap = useMemo(() => {
    if (!touchpointData) return {};
    const map: Record<string, string> = {};
    touchpointData.touchpoints.forEach((tp, i) => {
      map[tp] = touchpointData.touchpointLabels[i] || tp;
    });
    return map;
  }, [touchpointData]);

  const isLoading = metricsLoading || touchpointLoading || rewardLoading;

  // 보고 텍스트 생성
  const reportText = useMemo(() => {
    const metrics = metricsData?.data ?? [];
    const touchpointRows = touchpointData?.rows ?? [];

    if (isLoading || metrics.length === 0) return '';

    if (mode === 'daily') {
      const metric = metrics[0];
      const tpRow = touchpointRows.find(r => r.date === yesterday);
      const rewardDay = rewardData?.daily?.find(d => d.date === yesterday);
      return buildDailyReport(
        yesterday,
        metric,
        tpRow,
        touchpointLabelMap,
        snapshotData ?? undefined,
        rewardDay?.types ?? [],
      );
    }

    return buildPeriodReport(
      periodFrom,
      periodTo,
      metrics,
      touchpointRows,
      touchpointLabelMap,
      snapshotData ?? undefined,
      rewardData?.daily ?? [],
    );
  }, [mode, isLoading, metricsData, touchpointData, touchpointLabelMap, snapshotData, rewardData, yesterday, periodFrom, periodTo]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(reportText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">로딩 중...</div>;
  }

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <h3 className="text-base font-bold text-gray-900">보고용 대시보드</h3>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border overflow-hidden">
            <button
              onClick={() => setMode('daily')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                mode === 'daily'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              일일 보고 (D-1)
            </button>
            <button
              onClick={() => setMode('period')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                mode === 'period'
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              기간 보고 (14일)
            </button>
          </div>
        </div>
      </div>

      {/* 안내 */}
      <p className="text-xs text-gray-500">
        {mode === 'daily'
          ? `전일(${yesterday}) 데이터를 AI 전달용 텍스트로 생성합니다.`
          : `최근 14일(${periodFrom} ~ ${periodTo}) 데이터를 AI 전달용 텍스트로 생성합니다.`}
      </p>

      {/* 미리보기 */}
      <Card className="p-4">
        <pre className="text-xs leading-relaxed text-gray-800 whitespace-pre-wrap font-mono overflow-auto max-h-[600px]">
          {reportText || '데이터가 없습니다.'}
        </pre>
      </Card>

      {/* 복사 버튼 */}
      <div className="flex justify-center">
        <Button
          onClick={handleCopy}
          disabled={!reportText}
          className="flex items-center gap-2 px-6"
          variant={copied ? 'outline' : 'default'}
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" />
              복사 완료
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              전체 복사
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
